import React, {useEffect, useRef, ReactNode} from 'react'

interface VantaOptions {
  [key: string]: any
}

interface Props {
  children?: ReactNode
  className?: string
  options?: VantaOptions
  style?: React.CSSProperties
}

export default function VantaTopology({children, className, options = {}, style}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const vantaRef = useRef<any>(null)

  useEffect(() => {
    let mounted = true

    const loadScript = (src: string) =>
      new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve()
        const s = document.createElement('script')
        s.src = src
        s.async = true
        s.onload = () => resolve()
        s.onerror = () => reject(new Error(`Failed to load ${src}`))
        document.head.appendChild(s)
      })

    const initVanta = () => {
      const w = window as any
      if (w.VANTA && containerRef.current && !vantaRef.current) {
        vantaRef.current = w.VANTA.TOPOLOGY({
          el: containerRef.current,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          scale: 1.0,
          scaleMobile: 1.0,

          color: 0xd3d3d3,
          backgroundColor: 0xffffff,
          ...options,
        })
      }
    }

    ;(async () => {
      try {
        const w = window as any
        if (!w.THREE) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r134/three.min.js')
        }
        if (!w.p5) {
          await loadScript('https://cdnjs.cloudflare.com/ajax/libs/p5.js/1.1.9/p5.min.js')
        }
        await loadScript('https://cdn.jsdelivr.net/npm/vanta@latest/dist/vanta.topology.min.js')

        if (mounted) initVanta()
      } catch (err) {
        console.error('Error loading Vanta scripts:', err)
      }
    })()
    ;(window as any).setVanta = initVanta

    return () => {
      mounted = false
      if (vantaRef.current && typeof vantaRef.current.destroy === 'function') {
        try {
          vantaRef.current.destroy()
        } catch (e) {}
        vantaRef.current = null
      }
      try {
        delete (window as any).setVanta
      } catch (e) {}
    }
  }, [options])

  return (
    <div
      ref={containerRef}
      className={className ?? 'vanta-root'}
      style={{width: '100%', height: '100%', position: 'relative', ...style}}
    >
      {children}
    </div>
  )
}
